import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";
import type { EffortEstimate } from "../../api/client";

interface Props {
  estimate: EffortEstimate;
  track: "knowledge" | "portfolio";
  onTrackChange: (t: "knowledge" | "portfolio") => void;
}

export default function EstimateCard({ estimate, track, onTrackChange }: Props) {
  const { t } = useTranslation("roadmap");
  // Named `data`, not `t` — `t` is the useTranslation() binding above.
  const data = estimate[track];
  const [wkLo, wkHi] = data.weeks;
  const [hrLo, hrHi] = data.scoped_hours;
  const isPortfolio = track === "portfolio";
  const isRecommended = estimate.recommended_track === track;
  const trackLabel = (trk: "knowledge" | "portfolio") =>
    trk === "portfolio" ? t("EstimateCard.trackPortfolio") : t("EstimateCard.trackKnowledge");

  function handleToggle(_: React.MouseEvent, value: string | null) {
    if (value === "knowledge" || value === "portfolio") {
      onTrackChange(value);
    }
    // null means user clicked the already-active button — ignore
  }

  // Max weeks_high across all ladder rows, used to size the bar widths
  const maxWeeksHigh = data.ladder.length > 0
    ? Math.max(...data.ladder.map((r) => r.weeks_high))
    : 1;

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderRadius: 3,
        p: { xs: 2.5, md: 3.5 },
        bgcolor: "background.paper",
        boxShadow: theme.shadows[4],
        position: "relative",
        overflow: "hidden",
      })}
    >
      {/* Track toggle */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
        <Box>
          <Typography
            variant="caption"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: isRecommended ? "success.main" : "warning.main",
              bgcolor: isRecommended
                ? (theme) => alpha(theme.palette.success.main, 0.1)
                : (theme) => alpha(theme.palette.warning.main, 0.1),
              border: `1px solid`,
              borderColor: isRecommended ? "success.main" : "warning.main",
              borderRadius: 999,
              px: 1.25,
              py: 0.5,
            })}
          >
            <Box
              component="span"
              sx={(theme) => ({
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: isRecommended ? "success.main" : "warning.main",
                boxShadow: `0 0 6px ${isRecommended
                  ? alpha(theme.palette.success.main, 0.8)
                  : alpha(theme.palette.warning.main, 0.8)}`,
                display: "inline-block",
              })}
            />
            {isRecommended
              ? t("EstimateCard.recommendedForYou")
              : t("EstimateCard.recommendedTrack", { track: trackLabel(estimate.recommended_track) })}
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          value={track}
          onChange={handleToggle}
          size="small"
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 0.5,
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: 1.5,
              fontWeight: 500,
              px: 2,
              py: 0.75,
              color: "text.secondary",
              textTransform: "none",
              "&.Mui-selected": {
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: theme.shadows[1],
              },
            },
          })}
        >
          <ToggleButton value="knowledge">{t("EstimateCard.trackKnowledge")}</ToggleButton>
          <ToggleButton value="portfolio">{t("EstimateCard.trackPortfolio")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Headline weeks range */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, display: "block", mb: 0.5 }}
      >
        {t("EstimateCard.interviewReadyIn")}
      </Typography>
      <Box data-testid="estimate-headline" sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.75 }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: 500,
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            lineHeight: 0.95,
            letterSpacing: "-0.025em",
          }}
        >
          {wkLo}–{wkHi}
        </Typography>
        <Typography
          component="span"
          sx={{ fontWeight: 500, color: "success.main", letterSpacing: 0, fontSize: "1rem" }}
        >
          {t("EstimateCard.weeks")}
        </Typography>
      </Box>

      {/* Meta row: hours budget + distinction uplift */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={3}
        mt={3}
        pt={2.5}
        sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}` })}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.disabled", mb: 0.5 }}
          >
            {t("EstimateCard.effortBudget")}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            ~{hrLo}–{hrHi}{" "}
            <Typography component="span" color="text.secondary" sx={{ fontWeight: 400, fontSize: "0.85em" }}>
              {t("EstimateCard.focusedHrs")}
            </Typography>
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.disabled", mb: 0.5 }}
          >
            {t("EstimateCard.track")}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {isPortfolio ? t("EstimateCard.trackPortfolioPractitioner") : t("EstimateCard.trackKnowledge")}
          </Typography>
        </Box>

        {isPortfolio && data.distinction_uplift_hours[1] > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ display: "block", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.disabled", mb: 0.5 }}
            >
              {t("EstimateCard.distinction")}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              +{data.distinction_uplift_hours[0]}–{data.distinction_uplift_hours[1]}{" "}
              <Typography component="span" color="text.secondary" sx={{ fontWeight: 400, fontSize: "0.85em" }}>
                {t("EstimateCard.hrsOptional")}
              </Typography>
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Rationale */}
      {estimate.track_rationale && (
        <Stack
          direction="row"
          gap={1.25}
          mt={2.5}
          p={1.75}
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.success.main, 0.06),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          <Typography component="span" sx={{ color: "success.main", lineHeight: 1.5, flexShrink: 0 }}>◆</Typography>
          <Typography variant="body2" color="text.secondary">
            {estimate.track_rationale}
          </Typography>
        </Stack>
      )}

      {/* Hours ladder */}
      {data.ladder.length > 0 && (
        <Box mt={3.5}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
            {t("EstimateCard.whatChangesThis")}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
            {t("EstimateCard.fastestLever")}
          </Typography>

          {data.ladder.map((row) => {
            const barLeft = (row.weeks_low / maxWeeksHigh) * 100;
            const barWidth = Math.max(4, ((row.weeks_high - row.weeks_low) / maxWeeksHigh) * 100);

            return (
              <Box
                key={row.hours_band}
                sx={(theme) => ({
                  display: "grid",
                  gridTemplateColumns: "96px 1fr 120px",
                  alignItems: "center",
                  gap: 2,
                  py: 1.25,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                })}
              >
                {/* Band label — convert ASCII hyphen to EN dash for display */}
                <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                  {row.hours_band.replace("-", "–")} {t("EstimateCard.hrsAbbrev")}
                </Typography>

                {/* Bar */}
                <Box
                  sx={(theme) => ({
                    height: 9,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    position: "relative",
                    overflow: "hidden",
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${barLeft}%`,
                      width: `${barWidth}%`,
                      borderRadius: 3,
                      background: `linear-gradient(90deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                    })}
                  />
                </Box>

                {/* Week range as real DOM text for accessibility and selectability */}
                <Typography
                  sx={{
                    fontWeight: 500,
                    textAlign: "right",
                    fontSize: "0.9375rem",
                  }}
                >
                  {row.weeks_low}–{row.weeks_high} {t("EstimateCard.wksAbbrev")}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Caveats */}
      <Stack gap={1.25} mt={3}>
        <Stack direction="row" gap={1.25}>
          <Typography component="span" color="text.disabled" sx={{ flexShrink: 0 }}>↗</Typography>
          <Typography variant="caption" color="text.secondary">
            <Trans
              i18nKey="EstimateCard.prepTimeCaveat"
              t={t}
              components={[<strong key="strong0" />, <strong key="strong1" />]}
            />
          </Typography>
        </Stack>
        <Stack direction="row" gap={1.25}>
          <Typography component="span" color="text.disabled" sx={{ flexShrink: 0 }}>≈</Typography>
          <Typography variant="caption" color="text.secondary">
            <Trans
              i18nKey="EstimateCard.rangeCaveat"
              t={t}
              components={[<strong key="strong0" />]}
            />
          </Typography>
        </Stack>
      </Stack>

      {/* Calibration note */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: "block", mt: 3, fontStyle: "italic" }}
      >
        {estimate.calibration_note}
      </Typography>
    </Box>
  );
}
